import type { SetOptional } from 'type-fest'

import api from '@libs/api'

import DateUtils from '@utils/dates'
import { handleServiceError } from '@utils/errors'
import FileUtils from '@utils/files'
import { IUpdateCard } from '@utils/payment'
import { getTimeZone } from '@utils/timezones'
import {
  CouponDuration,
  EnterpriseBreakdown,
  EnterpriseCampaignType,
  EnterprisePlanSubscription,
  IApiData,
  ICard,
  ICoupon,
  IEnterpriseCampaign,
  IEnterpriseCustomProduct,
  IInvoice,
  IPaymentPlan,
  IPaymentSubscription,
  IPlan,
  IPlanPricing,
  IQueryFilters,
  IQueryFiltersStripe,
  ISubscriptionPlan,
  InternalSubscriptionStatus,
  PaymentPlanStatus,
  PaymentPlanType,
  PlanPeriod,
  QwilrPage,
} from '@utils/types'
import { buildQueryString, buildQueryStringStripe } from '@utils/url'

export interface IApiStripeData<T> extends Omit<IApiData<T>, 'count'> {
  data: T
  hasMore: boolean
  nextPageToken: string | undefined
}

interface ISubscribePlan {
  planId: string
  priceId: string
  period: PlanPeriod
  cardToken: string
  couponId?: string
  promotionCodeId?: string
  referralId?: string
}

interface ICreateCoupon {
  code?: string
  amountOff?: number
  percentOff?: number
  duration: CouponDuration
  discountedInvoices?: number
  vendorUserId: string
  planId?: string
  period?: PlanPeriod
}

interface ICreateStandardPlan {
  type: PaymentPlanType
  name: string
  alias: string
  highlighted: boolean
  status: PaymentPlanStatus
  pricing: IPlanPricing[]
  maxSolutions: number
  includedSupportHours: number
  supportHoursRate: number
}

export interface ICreateEnterprisePlanManually {
  name: string
  alias?: string
  vendorUserId: string
  cancelDate: Date
  platform: {
    includedStandardPlanId: string
    maxSolutions?: number
    includedSupportHours?: number
    supportHoursRate?: number
  }
  pricing: {
    amount: number
    installments: number
    financingFee: number
    gracePeriod: number
  }
  campaigns: {
    name: string
    numberOfLeads: number
    type: EnterpriseCampaignType
  }[]
  customProducts?: {
    name: string
    description: string
  }[]
}

export interface ICreateEnterpriseAdditionalService {
  name: string
  enterprisePlanId: string
  cancelDate: Date
  campaigns?: Pick<IEnterpriseCampaign, 'name' | 'numberOfLeads' | 'type'>[]
  customProducts?: Pick<IEnterpriseCustomProduct, 'name' | 'description'>[]
  pricing: {
    amount: number
    financingFee: number
    gracePeriod: number
    installments: number
  }
}

export interface ICreateEnterprisePlanFromQwilr {
  name: string
  cancelDate: string
  pricing: {
    name: string
    amount: number
    includedStandardPlanId: string
    features: {
      name: string
      amount?: number
    }[]
  }[]
  leadBuckets: {
    name: string
    description: string
  }[]
}

interface IUpdateStandardPlan {
  name: string
  alias?: string
  highlighted?: boolean
  status?: PaymentPlanStatus
  pricing: IPlanPricing[]
  maxSolutions: number
  includedSupportHours: number
  supportHoursRate: number
}

export type StandardPlanWithSupportHour = IPaymentPlan & {
  supportHourPlan?: IPaymentPlan
}

export type PaymentPlanWithEnterpriseSubscription = Exclude<
  IPaymentPlan,
  'pricingPerPeriod' | 'parentPlan'
> & {
  enterpriseSubscription: IPaymentSubscription & {
    standardPlanSubscription: IPaymentSubscription & {
      supportHourSubscription: IPaymentSubscription
    }
  }
  otherSubscriptions: IPaymentSubscription[]
  totalAmount: number
  subscriptionStatus: InternalSubscriptionStatus
}

type FindResponse<T extends unknown> = Omit<
  T,
  'createdDate' | 'updatedDate'
> & {
  createdDate: string
  updatedDate: string
  expirationDate: string
}

export type GetMyEnterpriseSubscriptionResponse = Omit<
  EnterprisePlanSubscription,
  'cancelDate'
> & {
  cancelDate: string
}

export type GetMyEnterpriseSubscriptionBreakdownResponse = Pick<
  IPaymentSubscription,
  'id' | 'name' | 'status'
>[]

const mapFindResponse = <T extends unknown>({
  createdDate,
  updatedDate,
  expirationDate,
  ...data
}: FindResponse<T>): any => {
  return {
    ...data,
    createdDate: new Date(createdDate),
    updatedDate: new Date(updatedDate),
    expirationDate: new Date(expirationDate),
  }
}

const createStandardPlan = async (
  data: ICreateStandardPlan
): Promise<IPaymentPlan> => {
  try {
    const response = (await api.post({
      path: '/payments/plans/standard',
      data,
    })) as IPaymentPlan

    return response
  } catch (err: any) {
    throw handleServiceError(err)
  }
}

const updateStandardPlan = async (
  planId: string,
  data: IUpdateStandardPlan
) => {
  try {
    await api.put({
      path: `/payments/plans/standard/${planId}`,
      data,
    })
  } catch (err: any) {
    throw handleServiceError(err)
  }
}

const getMyCards = async (): Promise<ICard[]> => {
  try {
    const response = (await api.get({
      path: '/payments/cards',
    })) as ICard[]

    return response
  } catch (err: any) {
    throw handleServiceError(err)
  }
}

const addCard = async ({
  cardToken,
}: {
  cardToken: string
}): Promise<ICard> => {
  try {
    const response = (await api.post({
      path: '/payments/cards',
      data: { cardToken },
    })) as ICard

    return response
  } catch (err: any) {
    throw handleServiceError(err)
  }
}

const updateCard = async ({
  cardId,
  data,
}: {
  cardId: string
  data: IUpdateCard
}): Promise<ICard> => {
  try {
    const response = (await api.put({
      path: `/payments/cards/${cardId}`,
      data,
    })) as ICard

    return response
  } catch (err: any) {
    throw handleServiceError(err)
  }
}

const setDefaultCard = async (cardId: string) => {
  try {
    await api.post({
      path: `/payments/cards/${cardId}/default`,
    })
  } catch (err: any) {
    throw handleServiceError(err)
  }
}

const deleteCard = async (cardId: string) => {
  try {
    await api.remove({
      path: `/payments/cards/${cardId}`,
    })
  } catch (err: any) {
    throw handleServiceError(err)
  }
}

const findStandardPlansAsVisitor = async (): Promise<IPlan[]> => {
  try {
    const response = (await api.get({
      path: '/payments/plans/standard/public',
      authenticate: false,
    })) as IPlan[]

    return DateUtils.replaceISODateWithDate(response)
  } catch (err: any) {
    throw handleServiceError(err)
  }
}

const createEnterprisePlanManually = async (
  data: ICreateEnterprisePlanManually
): Promise<IPaymentPlan> => {
  try {
    const response = (await api.post({
      path: '/payments/plans/enterprise',
      data,
    })) as IPaymentPlan

    return response
  } catch (err: any) {
    throw handleServiceError(err)
  }
}

const createEnterpriseAdditionalService = async (
  data: ICreateEnterpriseAdditionalService
): Promise<IPaymentPlan> => {
  try {
    const response = (await api.post({
      path: '/payments/plans/enterprise/additional-service',
      data,
    })) as IPaymentPlan

    return response
  } catch (err: any) {
    throw handleServiceError(err)
  }
}

const createEnterprisePlanFromQwilr = async (
  data: ICreateEnterprisePlanFromQwilr
): Promise<QwilrPage> => {
  try {
    const response = (await api.post({
      path: '/payments/plans/enterprise/qwilr',
      data,
    })) as QwilrPage

    return response
  } catch (err: any) {
    throw handleServiceError(err)
  }
}

const findStandardPlansAsAdmin = async (
  queryFilters?: SetOptional<IQueryFilters, 'page' | 'limit'>
): Promise<IApiData<IPaymentPlan[]>> => {
  const queryString = buildQueryString(queryFilters)

  try {
    const response = (await api.get({
      path: '/payments/plans/standard',
      queryString,
    })) as IApiData<FindResponse<IPaymentPlan>[]>

    return {
      ...response,
      data: DateUtils.replaceISODateWithDate(response.data),
    }
  } catch (err: any) {
    throw handleServiceError(err)
  }
}

const findEnterprisePlansAsAdmin = async (
  queryFilters?: SetOptional<IQueryFilters, 'page' | 'limit'>
): Promise<IApiData<PaymentPlanWithEnterpriseSubscription[]>> => {
  const queryString = buildQueryString(queryFilters)

  try {
    const response = (await api.get({
      path: '/payments/plans/enterprise',
      queryString,
    })) as IApiData<FindResponse<PaymentPlanWithEnterpriseSubscription>[]>

    return {
      ...response,
      data: DateUtils.replaceISODateWithDate(response.data),
    }
  } catch (err: any) {
    throw handleServiceError(err)
  }
}

const findStandardPlanAsAdmin = async (
  planId: string
): Promise<IPaymentPlan> => {
  try {
    const response = (await api.get({
      path: `/payments/plans/standard/${planId}`,
    })) as IPaymentPlan

    return DateUtils.replaceISODateWithDate(response)
  } catch (err: any) {
    throw handleServiceError(err)
  }
}

const findSupportHourPlanAsAdmin = async (
  planId: string
): Promise<IPaymentPlan> => {
  try {
    const response = (await api.get({
      path: `/payments/plans/support-hour/${planId}`,
    })) as IPaymentPlan

    return DateUtils.replaceISODateWithDate(response)
  } catch (err: any) {
    throw handleServiceError(err)
  }
}

const findEnterprisePlanAsAdmin = async (
  planId: string
): Promise<PaymentPlanWithEnterpriseSubscription> => {
  try {
    const response = (await api.get({
      path: `/payments/plans/enterprise/${planId}`,
    })) as PaymentPlanWithEnterpriseSubscription

    return DateUtils.replaceISODateWithDate(response)
  } catch (err: any) {
    throw handleServiceError(err)
  }
}

const fetchCsvData = async (
  queryFilters: SetOptional<IQueryFilters, 'page' | 'limit'>
) => {
  const queryString = {
    ...buildQueryString(queryFilters),
    timeZone: getTimeZone(),
  }

  try {
    const response = await api.csv({
      path: '/payments/subscriptions/csv',
      queryString,
    })

    FileUtils.download({
      content: response,
      fileName: 'subscriptions.csv',
      contentType: 'text/csv',
    })
  } catch (err: any) {
    throw handleServiceError(err)
  }
}

const findAllVendorSubscriptions = async (
  queryFilters?: SetOptional<IQueryFilters, 'page' | 'limit'>
): Promise<IApiData<IPaymentSubscription[]>> => {
  const queryString = buildQueryString(queryFilters)

  try {
    const response = (await api.get({
      path: '/payments/subscriptions',
      queryString,
    })) as IApiData<FindResponse<IPaymentSubscription>[]>

    return {
      ...response,
      data: response.data.map(mapFindResponse),
    }
  } catch (err: any) {
    throw handleServiceError(err)
  }
}

const subscribePlan = async (data: ISubscribePlan) => {
  try {
    await api.post({
      path: '/payments/subscribe',
      data,
    })
  } catch (err: any) {
    throw handleServiceError(err)
  }
}

const getMySubscription = async () => {
  try {
    const response = (await api.get({
      path: '/payments/subscriptions/me',
    })) as ISubscriptionPlan | undefined

    return response
  } catch (err: any) {
    throw handleServiceError(err)
  }
}

const findMyEnterpriseSubscription =
  async (): Promise<GetMyEnterpriseSubscriptionResponse> => {
    try {
      const response = await api.get({
        path: '/payments/subscriptions/enterprise/me',
      })

      return {
        ...response,
        cancelDate: new Date(response.cancelDate),
      }
    } catch (err: any) {
      throw handleServiceError(err)
    }
  }

const findMyEnterpriseSubscriptionBreakdown =
  async (): Promise<GetMyEnterpriseSubscriptionBreakdownResponse> => {
    try {
      const response = (await api.get({
        path: '/payments/subscriptions/enterprise/breakdown',
      })) as GetMyEnterpriseSubscriptionBreakdownResponse

      return response
    } catch (err: any) {
      throw handleServiceError(err)
    }
  }

const findMyEnterpriseSubscriptionBreakdownDetails = async (
  subscriptionId: string
) => {
  try {
    const response = (await api.get({
      path: `/payments/subscriptions/enterprise/breakdown/${subscriptionId}`,
    })) as EnterpriseBreakdown

    return response
  } catch (err: any) {
    throw handleServiceError(err)
  }
}

const getVendorActiveSubscriptionByUserId = async (
  vendorUserId: string
): Promise<ISubscriptionPlan | null> => {
  try {
    const response = (await api.get({
      path: `/payments/subscriptions/vendor/${vendorUserId}`,
    })) as ISubscriptionPlan

    return response
  } catch (err: any) {
    throw handleServiceError(err)
  }
}

const getSubscriptionById = async (subscriptionId: string) => {
  try {
    const response = (await api.get({
      path: `/payments/subscriptions/${subscriptionId}`,
    })) as ISubscriptionPlan

    return response
  } catch (err: any) {
    throw handleServiceError(err)
  }
}

const cancelSubscription = async ({
  entries,
  skipped,
}: {
  entries: { question?: string; answer: string }[]
  skipped: boolean
}) => {
  try {
    await api.put({
      path: '/payments/cancel-subscription',
      data: {
        entries,
        skipped,
      },
    })
  } catch (err: any) {
    throw handleServiceError(err)
  }
}

const reactivateSubscription = async () => {
  try {
    await api.post({
      path: '/payments/reactivate-subscription',
    })
  } catch (err: any) {
    throw handleServiceError(err)
  }
}

const getMyInvoices = async (
  queryFilters?:
    | SetOptional<IQueryFiltersStripe, 'nextPageToken' | 'limit'>
    | undefined
): Promise<IApiStripeData<IInvoice[]>> => {
  const queryString = buildQueryStringStripe(queryFilters)
  try {
    const response = (await api.get({
      path: '/payments/my-invoices',
      queryString,
    })) as IApiStripeData<IInvoice[]>

    return response
  } catch (err: any) {
    throw handleServiceError(err)
  }
}

const sendInvoice = async ({
  invoiceId,
}: {
  invoiceId: string
}): Promise<IInvoice> => {
  try {
    const response = (await api.post({
      path: `/payments/invoices/${invoiceId}/send`,
    })) as IInvoice

    return response
  } catch (err: any) {
    throw handleServiceError(err)
  }
}

const retryInvoicePayment = async ({
  invoiceId,
}: {
  invoiceId: string
}): Promise<IInvoice> => {
  try {
    const response = (await api.post({
      path: '/payments/retry-payment',
      data: { invoiceId },
    })) as IInvoice

    return response
  } catch (err: any) {
    throw handleServiceError(err)
  }
}

const downloadInvoice = async ({
  invoiceId,
  subscriptionId,
}: {
  invoiceId: string
  subscriptionId?: string
}): Promise<Buffer> => {
  try {
    return await api.get({
      path: `/payments/invoices/${invoiceId}/download/${subscriptionId}`,
      appendHeaders: {
        'Content-Type': 'application/json',
        Accept: 'application/pdf',
      },
      config: {
        responseType: 'arraybuffer',
      },
    })
  } catch (err: any) {
    throw handleServiceError(err)
  }
}

const getMyAvailableCoupon = async () => {
  try {
    const response = (await api.get({
      path: '/payments/coupon/me',
    })) as ICoupon | undefined

    return response
  } catch (err: any) {
    throw handleServiceError(err)
  }
}

const createVendorCoupon = async (data: ICreateCoupon) => {
  try {
    await api.post({
      path: '/payments/coupon',
      data,
    })
  } catch (err: any) {
    throw handleServiceError(err)
  }
}

const removeVendorCoupon = async ({
  couponId,
  vendorUserId,
}: {
  couponId: string
  vendorUserId?: string
}) => {
  try {
    await api.remove({
      path: `/payments/coupon/${couponId}/${vendorUserId}`,
    })
  } catch (err: any) {
    throw handleServiceError(err)
  }
}

export default {
  getMyCards,
  addCard,
  updateCard,
  setDefaultCard,
  deleteCard,
  createStandardPlan,
  updateStandardPlan,
  findStandardPlansAsVisitor,
  findStandardPlansAsAdmin,
  findEnterprisePlansAsAdmin,
  createEnterprisePlanManually,
  createEnterpriseAdditionalService,
  createEnterprisePlanFromQwilr,
  findStandardPlanAsAdmin,
  findSupportHourPlanAsAdmin,
  findEnterprisePlanAsAdmin,
  subscribePlan,
  getMySubscription,
  findMyEnterpriseSubscription,
  findMyEnterpriseSubscriptionBreakdown,
  findMyEnterpriseSubscriptionBreakdownDetails,
  findAllVendorSubscriptions,
  getVendorActiveSubscriptionByUserId,
  getSubscriptionById,
  cancelSubscription,
  reactivateSubscription,
  getMyInvoices,
  sendInvoice,
  retryInvoicePayment,
  downloadInvoice,
  fetchCsvData,
  getMyAvailableCoupon,
  createVendorCoupon,
  removeVendorCoupon,
}
